import { Injectable, InjectionToken, inject, Optional, Inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Injection token for the upload API base URL.
 * Each app provides this in its app.config.ts to route uploads to the correct backend.
 * Defaults to accountingUrl if not provided.
 */
export const UPLOAD_API_BASE_URL = new InjectionToken<string>('UPLOAD_API_BASE_URL');

// ============================================================================
// Request/Response Interfaces
// ============================================================================

export interface UploadJobResponse {
  id: string;
  module: string;
  module_name: string;
  mode: 'create' | 'update';
  original_file_name: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'partial';
  status_label: string;
  total_rows: number;
  processed_rows: number;
  success_count: number;
  error_count: number;
  updated_count: number;
  progress: number;
  file_size_bytes: number;
  file_size_display: string;
  error_message?: string;
  has_error_report: boolean;
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

export interface UploadValidationError {
  row: number;
  column: string;
  value: string;
  message: string;
}

export interface UploadJobDetailResponse extends UploadJobResponse {
  validation_errors?: UploadValidationError[];
}

export interface UploadJobListResponse {
  items: UploadJobResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface UploadJobStats {
  total_jobs: number;
  pending_jobs: number;
  processing_jobs: number;
  completed_jobs: number;
  failed_jobs: number;
  partial_jobs: number;
}

export interface UploadConfigResponse {
  max_rows_per_upload: number;
  max_concurrent_uploads: number;
  max_file_size_mb: number;
  chunk_size: number;
  file_retention_days: number;
}

export interface ErrorReportUrlResponse {
  download_url: string;
  expires_at: string;
}

export interface ApiResponse<T> {
  status: string;
  message: string;
  data: T;
}

export interface UploadJobFilters {
  module?: string;
  status?: string;
  limit?: number;
  offset?: number;
  page?: number;
  page_size?: number;
}

// ============================================================================
// Upload API Service
// ============================================================================

/**
 * Service for managing background upload jobs.
 * Provides methods to create, list, retrieve, and track upload jobs.
 */
@Injectable({
  providedIn: 'root'
})
export class UploadApiService {
  private readonly API_BASE_URL: string;

  constructor(
    private http: HttpClient,
    @Optional() @Inject(UPLOAD_API_BASE_URL) uploadBaseUrl: string | null,
  ) {
    this.API_BASE_URL = uploadBaseUrl || environment.accountingUrl;
  }

  /**
   * Create a new background upload job.
   * The job will be processed in the background by the worker pool.
   *
   * @param file File to upload
   * @param module Module name (e.g., 'vendor', 'client', 'item')
   * @param mode Upload mode: 'create' or 'update'
   * @param formTemplateId Optional form template ID for validation
   * @returns Observable with created job details
   */
  createUploadJob(
    file: File,
    module: string,
    mode: 'create' | 'update' = 'create',
    formTemplateId?: number | string
  ): Observable<ApiResponse<UploadJobResponse>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('module', module);
    formData.append('mode', mode);
    formData.append('background', 'true'); // Process in background
    if (formTemplateId) {
      formData.append('form_template_id', formTemplateId.toString());
    }

    return this.http.post<ApiResponse<UploadJobResponse>>(
      `${this.API_BASE_URL}/api/v1/uploads`,
      formData,
      { withCredentials: true }
    );
  }

  /**
   * List upload jobs for the current user.
   *
   * @param filters Optional filters for module, status, pagination
   * @returns Observable with paginated list of jobs
   */
  listUploadJobs(filters?: UploadJobFilters): Observable<ApiResponse<UploadJobListResponse>> {
    let params = new HttpParams();

    if (filters) {
      if (filters.module) params = params.set('module', filters.module);
      if (filters.status) params = params.set('status', filters.status);
      if (filters.limit) params = params.set('limit', filters.limit.toString());
      if (filters.offset !== undefined) params = params.set('offset', filters.offset.toString());
      if (filters.page) params = params.set('page', filters.page.toString());
      if (filters.page_size) params = params.set('page_size', filters.page_size.toString());
    }

    return this.http.get<ApiResponse<UploadJobListResponse>>(
      `${this.API_BASE_URL}/api/v1/uploads`,
      { params, withCredentials: true }
    );
  }

  /**
   * Get a single upload job by ID with validation errors.
   *
   * @param jobId Upload job ID
   * @returns Observable with job details including validation errors
   */
  getUploadJob(jobId: number | string): Observable<ApiResponse<UploadJobDetailResponse>> {
    return this.http.get<ApiResponse<UploadJobDetailResponse>>(
      `${this.API_BASE_URL}/api/v1/uploads/${jobId}`,
      { withCredentials: true }
    );
  }

  /**
   * Get upload job statistics.
   *
   * @returns Observable with job statistics
   */
  getUploadStats(): Observable<ApiResponse<UploadJobStats>> {
    return this.http.get<ApiResponse<UploadJobStats>>(
      `${this.API_BASE_URL}/api/v1/uploads/stats`,
      { withCredentials: true }
    );
  }

  /**
   * Get tenant upload configuration.
   *
   * @returns Observable with upload configuration
   */
  getUploadConfig(): Observable<ApiResponse<UploadConfigResponse>> {
    return this.http.get<ApiResponse<UploadConfigResponse>>(
      `${this.API_BASE_URL}/api/v1/uploads/config`,
      { withCredentials: true }
    );
  }

  /**
   * Get error report download URL for a job with errors.
   *
   * @param jobId Upload job ID
   * @returns Observable with download URL and expiration
   */
  getErrorReportUrl(jobId: number | string): Observable<ApiResponse<ErrorReportUrlResponse>> {
    return this.http.get<ApiResponse<ErrorReportUrlResponse>>(
      `${this.API_BASE_URL}/api/v1/uploads/${jobId}/error-report`,
      { withCredentials: true }
    );
  }

  /**
   * Cancel or delete an upload job.
   * - Pending jobs will be cancelled
   * - Completed/failed jobs will be soft-deleted
   * - Processing jobs cannot be cancelled
   *
   * @param jobId Upload job ID
   * @returns Observable with success/error response
   */
  deleteUploadJob(jobId: number | string): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(
      `${this.API_BASE_URL}/api/v1/uploads/${jobId}`,
      { withCredentials: true }
    );
  }

  /**
   * Retry a failed upload job.
   * Resets the job status and requeues it for processing.
   *
   * @param jobId Upload job ID
   * @returns Observable with success/error response
   */
  retryUploadJob(jobId: number | string): Observable<ApiResponse<null>> {
    return this.http.post<ApiResponse<null>>(
      `${this.API_BASE_URL}/api/v1/uploads/${jobId}/retry`,
      {},
      { withCredentials: true }
    );
  }

  /**
   * Download error report for a job.
   * First fetches the presigned URL, then triggers browser download.
   *
   * @param jobId Upload job ID
   */
  async downloadErrorReport(jobId: number | string): Promise<void> {
    const response = await this.getErrorReportUrl(jobId).toPromise();

    if (response?.data?.download_url) {
      window.location.href = response.data.download_url;
    }
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  /**
   * Get human-readable status label
   */
  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      'pending': 'Pending',
      'processing': 'Processing',
      'completed': 'Completed',
      'failed': 'Failed',
      'cancelled': 'Cancelled',
      'partial': 'Partially Completed'
    };
    return labels[status] || status;
  }

  /**
   * Get status badge severity for PrimeNG
   */
  getStatusSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    const severities: Record<string, 'success' | 'info' | 'warn' | 'danger' | 'secondary'> = {
      'pending': 'secondary',
      'processing': 'info',
      'completed': 'success',
      'failed': 'danger',
      'cancelled': 'warn',
      'partial': 'warn'
    };
    return severities[status] || 'secondary';
  }

  /**
   * Format file size to human-readable string
   */
  formatFileSize(bytes?: number): string {
    if (!bytes || bytes === 0) return '-';

    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }

  /**
   * Get human-readable module name
   */
  getModuleLabel(module: string): string {
    const labels: Record<string, string> = {
      'vendor': 'Vendors',
      'client': 'Clients',
      'item': 'Items',
      'account': 'Accounts',
      'vendor_invoice': 'Vendor Invoices',
      'client_invoice': 'Client Invoices',
      'vendor_payment': 'Vendor Payments',
      'client_payment': 'Client Payments',
      'vendor_debit_note': 'Vendor Debit Notes',
      'client_credit_note': 'Client Credit Notes',
      'credit_note': 'Credit Notes',
      'client_order': 'Sales Orders',
      'journal_entry': 'Journal Entries',
      'journal_voucher': 'Journal Vouchers',
      'opening_stock': 'Opening Stock',
      'grn': 'GRN',
      'vendor_po': 'Purchase Orders',
      'vendor_return': 'Vendor Returns',
      'client_return': 'Client Returns',
      'client_refund': 'Client Refunds'
    };
    return labels[module] || module.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  /**
   * Get mode label
   */
  getModeLabel(mode: string): string {
    return mode === 'update' ? 'Update' : 'Create';
  }

  /**
   * Check if a job is still active (pending or processing)
   */
  isJobActive(job: UploadJobResponse): boolean {
    return job.status === 'pending' || job.status === 'processing';
  }

  /**
   * Check if a job has completed (successfully or with errors)
   */
  isJobCompleted(job: UploadJobResponse): boolean {
    return job.status === 'completed' || job.status === 'partial' || job.status === 'failed';
  }

  /**
   * Format date string for display
   */
  formatDate(dateString?: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString();
  }
}
